import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ClockIcon, SearchXIcon } from 'lucide-react';
import { SearchField } from '../components/SearchField';
import { NumberCard } from '../components/NumberCard';
import { NumberPill } from '../components/NumberPill';
import { SectionHeading } from '../components/SectionHeading';
import { getNumber, popularNumberIds } from '../data/numbers';
import { resolveExact, searchNumbers, searchSuggestions } from '../utils/search';
import { useLibrary } from '../contexts/LibraryContext';
const ease = [0.23, 1, 0.32, 1] as const;
export function Search() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');
  const {
    recentSearches,
    clearRecentSearches
  } = useLibrary();
  const results = useMemo(() => searchNumbers(query), [query]);
  const update = (value: string) => {
    setQuery(value);
    if (value.trim()) setParams({
      q: value.trim()
    }, {
      replace: true
    });else setParams({}, {
      replace: true
    });
  };
  const submit = () => {
    const exact = resolveExact(query);
    if (exact) navigate(`/number/${exact.id}`);else if (results.length > 0) navigate(`/number/${results[0].entry.id}`);
  };
  const recentEntries = recentSearches.map(item => getNumber(item.numberId));
  const hasQuery = query.trim().length > 0;
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.26,
    ease
  }} data-id="element-200" data-mp="3r2fn80lmu6lb">
      <h1 className="text-[26px] font-light leading-tight tracking-tight text-white" data-id="element-201" data-mp="3w0h7upypidyw">
        Find your number
      </h1>
      <p className="mt-2 text-[13.5px] leading-relaxed text-lav-dim" data-id="element-202" data-mp="3vws09emsxvn">
        Try 111, 11:11, 1-1-1-1 — or a word like “portal”, “love” or “abundance”.
      </p>

      <div className="mt-5" data-id="element-203" data-mp="1fc3j0jkshfex">
        <SearchField value={query} onChange={update} onSubmit={submit} autoFocus placeholder="Search angel number…" data-id="element-204" data-mp="1ffhmxw93pwoh" />
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-205" data-mp="37eof916vz25g">
        {hasQuery ? <motion.section key="results" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.22,
        ease
      }} className="mt-7" aria-live="polite" data-id="element-206" data-mp="3ldfq8eqgc7pd">
            {results.length === 0 ? <div className="glass flex flex-col items-center rounded-3xl px-6 py-10 text-center" data-id="element-207" data-mp="2omn7tag1q3kf">
                <SearchXIcon className="h-6 w-6 text-lav-dim" strokeWidth={1.6} aria-hidden="true" data-id="element-208" data-mp="32hpieavu8h3k" />
                <p className="mt-3 text-[15px] font-semibold text-white" data-id="element-209" data-mp="29zkab3rq3ux3">Nothing found for “{query}”</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-lav-dim" data-id="element-210" data-mp="250h2jbkczalf">
                  Try a repeating number such as 333, a clock pattern like 22:22, or a theme word.
                </p>
                <Link to="/explore" className="mt-5 rounded-full border border-aura/40 bg-aura/[0.12] px-5 py-2.5 text-[13px] font-semibold text-white transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-aura/20 hover:shadow-[0_0_24px_-8px_rgba(139,92,246,0.9)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-211" data-mp="p954qwypl8tu">
                  Browse the library
                </Link>
              </div> : <>
                <p className="mb-3 text-[12px] font-medium uppercase tracking-[0.16em] text-lav-dim" data-id="element-212" data-mp="u916epjxoo68">
                  {results.length} {results.length === 1 ? 'match' : 'matches'}
                </p>
                <div className="flex flex-col gap-2.5" data-id="element-213" data-mp="3ey7cabcysvx1">
                  {results.map((result, index) => <motion.div key={result.entry.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease,
              delay: Math.min(index * 0.035, 0.28)
            }} data-id="element-214" data-mp="3jycz9e8ahu8s" data-mpi={index}>
                      <NumberCard entry={result.entry} variant={result.reason === 'exact' ? 'feature' : 'row'} showSave={result.reason !== 'exact'} data-id="element-215" data-mp="3qfokonfosxzx" />
                    </motion.div>)}
                </div>
              </>}
          </motion.section> : <motion.div key="idle" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-216" data-mp="cyxlzw0cishd">
            {recentEntries.length > 0 && <section className="mt-8" data-id="element-217" data-mp="2sqlhro3mieyl">
                <div className="mb-3 flex items-baseline justify-between gap-3" data-id="element-218" data-mp="2spjuml9c220i">
                  <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-white" data-id="element-219" data-mp="2io96zfzs42wn">
                    <ClockIcon className="h-3.5 w-3.5 text-lav-dim" strokeWidth={2} aria-hidden="true" data-id="element-220" data-mp="2nmsq78mzn0lb" />
                    Recently searched
                  </h2>
                  <button type="button" onClick={clearRecentSearches} className="rounded-full text-[12px] font-medium text-lav-dim transition-colors duration-200 ease-smooth hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-221" data-mp="2xclz21vqx1lv">
                    Clear
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2.5" data-id="element-222" data-mp="3g9wx5j5n5ozq">
                  {recentEntries.map(entry => entry && <NumberCard key={entry.id} entry={entry} variant="tile" data-id="element-223" data-mp="1gzoupc5642kv" />)}
                </div>
              </section>}

            <section className="mt-8" data-id="element-224" data-mp="1dvprds4haabg">
              <SectionHeading title="Explore popular numbers" action={{
            label: 'All numbers',
            to: '/explore'
          }} data-id="element-225" data-mp="1dwrc6obaqb6d" />
              <div className="grid grid-cols-4 gap-2.5" data-id="element-226" data-mp="1nuyk6hnlxiw8">
                {popularNumberIds.map(id => <NumberPill key={id} id={id} label={getNumber(id)?.displayNumber ?? id} data-id="element-227" data-mp="1iv2m0z1lclol" />)}
              </div>
              <div className="mt-2.5 grid grid-cols-3 gap-2.5" data-id="element-228" data-mp="1wtirkmnpw655">
                {['1111', '1212', '1234'].map(id => <NumberPill key={id} id={id} label={getNumber(id)?.displayNumber ?? id} subtle data-id="element-229" data-mp="1ru688bw21h7b" />)}
              </div>
            </section>

            <section className="mt-8" data-id="element-230" data-mp="21n4x7uvgr9rg">
              <h2 className="mb-3 text-[15px] font-semibold tracking-tight text-white" data-id="element-231" data-mp="21o6a84w1i6bt">Search by theme</h2>
              <div className="flex flex-wrap gap-2" data-id="element-232" data-mp="2bjzuybjwx64o">
                {searchSuggestions.map(word => <button key={word} type="button" onClick={() => update(word)} className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-2 text-[12.5px] font-medium text-lav transition-[background-color,border-color,color] duration-200 ease-smooth hover:border-aura/45 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-aura-bright/70" data-id="element-233" data-mp="2u8aa26i32boy">
                    {word}
                  </button>)}
              </div>
            </section>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}